import { memoize } from 'lodash-es'
import { Cache } from './cache'
import { deepFreeze } from './freeze'
import { Loader } from './loader'
import type { LoaderStatic } from './loader-static'
import { Resolver } from './resolver'
import type { Adapters, Keys } from './types'

export type ConfigClientArgs =
	| {
			baseUrl?: string
			URL?: string
			adapters?: Adapters | null
			loader?: LoaderStatic
	  }
	| undefined

export class ConfigClient {
	private loader: Loader | LoaderStatic
	private cache: Cache
	private resolver: Resolver

	constructor(args: ConfigClientArgs = {}) {
		this.loader = args.loader
			? args.loader
			: new Loader({ baseURL: args.baseUrl, URL: args.URL })
		this.cache = new Cache()
		this.resolver = new Resolver({ cache: this.cache })

		if (args.adapters) {
			this.setAdapters(args.adapters)
		}
	}

	load = async () => {
		if (this.loader.state.status !== 'idle') {
			return
		}

		const raw: any = await this.loader.load()

		this.cache.updateRaw(deepFreeze(raw))
	}

	setAdapters = (adapters: Adapters | null) => {
		this.resolver.setAdapters(adapters)
	}

	preload = (keys?: Keys) => {
		if (this.loader.state.status !== 'loaded') {
			this.loader.on('load', () => this.preload(keys))

			return
		}

		for (const key of keys ?? []) {
			this.resolver.resolve(key)
		}
	}

	read = memoize(
		<Result extends readonly unknown[]>(keys: Keys): Result => {
			return this.resolver.resolve<Result>(...keys)
		},
		(keys) => {
			return (keys.length > 1 ? keys.join(',') : keys[0]) ?? ''
		},
	)

	subscribe = (key: string, callback: () => void): (() => void) => {
		return this.cache.subscribe(key, callback)
	}

	get entry() {
		return this.loader.state
	}
}
